/**
 * The one year picker of the app. Years are always a closed list — a project's term or the union
 * of a company's terms — so a year that cannot hold data (1990) is not offerable in the first
 * place. With nothing to offer it degrades to today's year rather than to an empty dropdown.
 */

import { useTranslation } from "react-i18next";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { berlinToday } from "@/shared/dates";

export interface YearSelectProps {
  value: number;
  onChange: (year: number) => void;
  /** Offered years, newest first. */
  years: number[];
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  testId?: string;
  /**
   * When true, labels past years as retrospective and current/future as live
   * (Live Tracking year picker).
   */
  showTrackingMode?: boolean;
}

export function YearSelect({
  value,
  onChange,
  years,
  ariaLabel,
  className,
  disabled,
  testId,
  showTrackingMode = false,
}: YearSelectProps) {
  const { t } = useTranslation();
  const currentBerlinYear = Number(berlinToday().slice(0, 4));
  const options = years.length > 0 ? years : [currentBerlinYear];
  const selected = options.includes(value) ? value : options[0]!;

  function labelFor(year: number): string {
    if (!showTrackingMode) return String(year);
    const mode = year < currentBerlinYear ? t("projectLive.retrospective") : t("projectLive.live");
    return t("projectLive.yearWithMode", { year, mode });
  }

  return (
    <Select
      value={String(selected)}
      onValueChange={(next) => onChange(Number(next))}
      disabled={disabled}
    >
      <SelectTrigger className={className ?? "w-28"} aria-label={ariaLabel} data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((year) => (
          <SelectItem key={year} value={String(year)}>
            {labelFor(year)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
